import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { X, Image as ImageIcon } from 'lucide-react';
export function Button({ variant = 'secondary', size = 'md', icon: Icon, loading = false, children, className, disabled, ...props }) {
    return (_jsxs("button", { className: clsx('inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed', {
            'bg-blue-600 text-white hover:bg-blue-700': variant === 'primary',
            'bg-gray-100 text-gray-900 hover:bg-gray-200': variant === 'secondary',
            'bg-red-600 text-white hover:bg-red-700': variant === 'danger',
            'text-gray-600 hover:text-gray-900 hover:bg-gray-100': variant === 'ghost',
            'px-2.5 py-1.5 text-sm': size === 'sm',
            'px-3 py-2 text-sm': size === 'md',
            'px-4 py-2.5 text-base': size === 'lg',
        }, className), disabled: disabled || loading, ...props, children: [loading ? (_jsx("div", { className: "w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" })) : Icon ? (_jsx(Icon, { size: 16 })) : null, children] }));
}
export function Input({ label, error, icon: Icon, className, ...props }) {
    return (_jsxs("div", { className: "space-y-1", children: [label && _jsx("label", { className: "text-sm font-medium text-gray-700", children: label }), _jsxs("div", { className: "relative", children: [Icon && (_jsx("div", { className: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none", children: _jsx(Icon, { size: 16, className: "text-gray-400" }) })), _jsx("input", { className: clsx('block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm', Icon && 'pl-10', error && 'border-red-300 focus:border-red-500 focus:ring-red-500', className), ...props })] }), error && _jsx("p", { className: "text-sm text-red-600", children: error })] }));
}
export function Select({ label, error, options, className, ...props }) {
    return (_jsxs("div", { className: "space-y-1", children: [label && _jsx("label", { className: "text-sm font-medium text-gray-700", children: label }), _jsx("select", { className: clsx('block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm', error && 'border-red-300 focus:border-red-500 focus:ring-red-500', className), ...props, children: options.map(option => (_jsx("option", { value: option.value, children: option.label }, option.value))) }), error && _jsx("p", { className: "text-sm text-red-600", children: error })] }));
}
export function Textarea({ label, error, className, ...props }) {
    return (_jsxs("div", { className: "space-y-1", children: [label && _jsx("label", { className: "text-sm font-medium text-gray-700", children: label }), _jsx("textarea", { className: clsx('block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm resize-none', error && 'border-red-300 focus:border-red-500 focus:ring-red-500', className), ...props }), error && _jsx("p", { className: "text-sm text-red-600", children: error })] }));
}
export function Card({ children, className }) {
    return (_jsx("div", { className: clsx('bg-white rounded-lg border border-gray-200 shadow-sm', className), children: children }));
}
export function Alert({ variant = 'info', children, onDismiss }) {
    return (_jsx("div", { className: clsx('rounded-md p-4 text-sm border', {
            'bg-blue-100 text-blue-900 border-blue-300': variant === 'info',
            'bg-green-100 text-green-900 border-green-300': variant === 'success',
            'bg-yellow-100 text-yellow-900 border-yellow-300': variant === 'warning',
        }), style: variant === 'error' ? {
            backgroundColor: '#fee2e2',
            color: '#7f1d1d',
            borderColor: '#fca5a5'
        } : undefined, children: _jsxs("div", { className: "flex justify-between items-start", children: [_jsx("div", { children: children }), onDismiss && (_jsx("button", { onClick: onDismiss, className: "ml-2 flex-shrink-0 text-current opacity-60 hover:opacity-100", children: "\u2715" }))] }) }));
}
export function TagChip({ tag, onRemove, variant = 'default' }) {
    return (_jsxs("span", { className: clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium', {
            'bg-gray-100 text-gray-800': variant === 'default',
            'bg-blue-100 text-blue-800': variant === 'blue',
            'bg-green-100 text-green-800': variant === 'green',
            'bg-purple-100 text-purple-800': variant === 'purple',
        }), children: [tag, onRemove && (_jsx("button", { onClick: onRemove, className: "ml-1 hover:text-red-600 focus:outline-none", children: _jsx(X, { size: 12 }) }))] }));
}
export function TagInput({ tags, availableTags = [], onTagsChange, placeholder = "Add tags...", className }) {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState(-1);
    const inputRef = useRef(null);
    const suggestionRefs = useRef([]);
    // Get filtered suggestions based on input
    useEffect(() => {
        if (inputValue.trim() && availableTags && tags) {
            const filtered = availableTags
                .filter(tag => tag.toLowerCase().includes(inputValue.toLowerCase()) &&
                !tags.map(t => t.toLowerCase()).includes(tag.toLowerCase()))
                .slice(0, 5);
            setSuggestions(filtered);
            setShowSuggestions(filtered.length > 0);
            setSelectedSuggestion(-1);
        }
        else {
            setSuggestions([]);
            setShowSuggestions(false);
            setSelectedSuggestion(-1);
        }
    }, [inputValue, availableTags, tags]);
    const addTag = (tagToAdd) => {
        const normalizedTag = tagToAdd.trim();
        const currentTags = tags || [];
        if (normalizedTag && !currentTags.map(t => t.toLowerCase()).includes(normalizedTag.toLowerCase())) {
            onTagsChange([...currentTags, normalizedTag]);
        }
        setInputValue('');
        setShowSuggestions(false);
        setSelectedSuggestion(-1);
        inputRef.current?.focus();
    };
    const removeTag = (index) => {
        const currentTags = tags || [];
        onTagsChange(currentTags.filter((_, i) => i !== index));
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedSuggestion >= 0 && suggestions[selectedSuggestion]) {
                addTag(suggestions[selectedSuggestion]);
            }
            else if (inputValue.trim()) {
                addTag(inputValue);
            }
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedSuggestion(prev => prev < suggestions.length - 1 ? prev + 1 : prev);
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedSuggestion(prev => prev > 0 ? prev - 1 : -1);
        }
        else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setSelectedSuggestion(-1);
        }
        else if (e.key === 'Backspace' && !inputValue && (tags || []).length > 0) {
            removeTag((tags || []).length - 1);
        }
    };
    // Scroll selected suggestion into view
    useEffect(() => {
        if (selectedSuggestion >= 0 && suggestionRefs.current[selectedSuggestion]) {
            suggestionRefs.current[selectedSuggestion]?.scrollIntoView({
                block: 'nearest',
            });
        }
    }, [selectedSuggestion]);
    return (_jsxs("div", { className: clsx('relative', className), children: [_jsx("div", { className: "min-h-[2.5rem] p-2 border border-gray-300 rounded-md bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500", children: _jsxs("div", { className: "flex flex-wrap gap-1 items-center", children: [(tags || []).map((tag, index) => (_jsx(TagChip, { tag: tag, onRemove: () => removeTag(index), variant: "blue" }, `${tag}-${index}`))), _jsx("input", { ref: inputRef, type: "text", value: inputValue, onChange: (e) => setInputValue(e.target.value), onKeyDown: handleKeyDown, onFocus: () => inputValue && setShowSuggestions(suggestions.length > 0), onBlur: () => setTimeout(() => setShowSuggestions(false), 200), placeholder: (tags || []).length === 0 ? placeholder : '', className: "flex-1 min-w-[120px] outline-none bg-transparent text-sm" })] }) }), showSuggestions && suggestions.length > 0 && (_jsx("div", { className: "absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto", children: suggestions.map((suggestion, index) => (_jsx("button", { ref: (el) => { suggestionRefs.current[index] = el; }, onClick: () => addTag(suggestion), className: clsx('w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none', selectedSuggestion === index && 'bg-blue-50'), children: suggestion }, suggestion))) }))] }));
}
export function Thumbnail({ src, alt = 'Thumbnail', width = 120, height = 90, className, fallback }) {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    // Reset error state when src changes
    useEffect(() => {
        setImageError(false);
        setImageLoaded(false);
    }, [src]);
    const defaultFallback = (_jsx("div", { className: clsx('flex items-center justify-center bg-gray-100 border border-gray-200 rounded', className), style: { width, height }, children: _jsx(ImageIcon, { size: 24, className: "text-gray-400" }) }));
    if (!src || imageError) {
        return fallback || defaultFallback;
    }
    return (_jsxs("div", { className: "relative", children: [_jsx("img", { src: src, alt: alt, width: width, height: height, onLoad: () => setImageLoaded(true), onError: () => setImageError(true), className: clsx('object-cover border border-gray-200 rounded transition-opacity duration-200', imageLoaded ? 'opacity-100' : 'opacity-0', className) }), !imageLoaded && !imageError && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-gray-100 border border-gray-200 rounded animate-pulse", style: { width, height }, children: _jsx(ImageIcon, { size: 20, className: "text-gray-400" }) }))] }));
}
